import { useState } from "react";
import api from "../api/api";
import { useNavigate } from "react-router-dom";
import { X, LogIn, UserPlus } from "lucide-react";

export default function LoginModal({ isOpen, onClose, setUser, setToast }) {
    const [mode, setMode] = useState("login");
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState("");

    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
    });

    if (!isOpen) return null;

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: "" });
    };

    const validate = () => {
        let newErrors = {};

        if (mode === "register" && !form.name.trim()) {
            newErrors.name = "Full name is required";
        }

        if (!form.email.trim()) {
            newErrors.email = "Email is required";
        }

        if (!form.password.trim()) {
            newErrors.password = "Password is required";
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);

        try {
            // REGISTER

            if (mode === "register") {
                await api.post("/register", {
                    name: form.name,
                    email: form.email,
                    password: form.password,
                    password_confirmation: form.password,
                });

                setMode("login");
                setLoading(false);
                onClose();
                return;
            }

            // LOGIN
            const res = await api.post("/login", {
                email: form.email,
                password: form.password,
            });

            const userData = res.data.user;

            localStorage.setItem("user", JSON.stringify(userData));
            setUser(userData);

            setLoading(false);

            setToast({
                type: "success",
                message: `Welcome ${userData.name}, you have successfully logged in 🎉`,
            });

            onClose();
            navigate("/profile");

        } catch (err) {
            console.error(err);
            setLoading(false);
            setErrors({
                general: "Invalid credentials or server error"
            });
        }
    };



    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">

            {/* MODAL CARD */}
            <div className="relative w-[92%] max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 p-6">

                {/* CLOSE */}
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                >
                    <X size={18} />
                </button>

                {/* TITLE */}
                <div className="text-center mb-6">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-700 flex items-center justify-center text-white">
                        {mode === "login" ? <LogIn size={20} /> : <UserPlus size={20} />}
                    </div>

                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {mode === "register" ? "Create Account" : "Welcome Back"}
                    </h2>

                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {mode === "register"
                            ? "Sign up to start booking hotels"
                            : "Login to continue your journey"}
                    </p>
                </div>

                {/* ERROR */}
                {errors.general && (
                    <div className="mb-4 text-sm text-red-500 bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">
                        {errors.general}
                    </div>
                )}

                {/* FORM */}
                <form onSubmit={handleSubmit} className="space-y-4">

                    {/* NAME */}
                    {mode === "register" && (
                        <div>
                            <input
                                name="name"
                                placeholder="Full Name"
                                onChange={handleChange}
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                            {errors.name && (
                                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                            )}
                        </div>
                    )}

                    {/* EMAIL */}
                    <div>
                        <input
                            name="email"
                            type="email"
                            placeholder="Email Address"
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        {errors.email && (
                            <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                        )}
                    </div>

                    {/* PASSWORD */}
                    <div>
                        <input
                            name="password"
                            type="password"
                            placeholder="Password"
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        {errors.password && (
                            <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                        )}
                    </div>

                    {/* SUBMIT */}
                    <button
                        disabled={loading}
                        className="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-700 hover:shadow-lg hover:scale-[1.02] transition disabled:opacity-60"
                    >
                        {loading
                            ? "Please wait..."
                            : mode === "register"
                                ? "Create Account"
                                : "Login"}
                    </button>
                </form>

                {/* SWITCH MODE */}
                <div className="text-center mt-5 text-sm text-gray-600 dark:text-gray-400">
                    {mode === "login" ? (
                        <button
                            onClick={() => setMode("register")}
                            className="text-blue-600 hover:underline"
                        >
                            New here? Create account
                        </button>
                    ) : (
                        <button
                            onClick={() => setMode("login")}
                            className="text-blue-600 hover:underline"
                        >
                            Already have an account? Login
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}